import type { MessageKey } from './i18n'
import type { Person } from './types'

/** Up to two letters of a name, for an avatar; titles such as "Dr." in it count only when it is nothing else. */
export function initials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean)
  const named = words.filter((word) => !word.endsWith('.'))
  const letters = (named.length ? named : words).slice(0, 2).map((word) => [...word][0]!)
  return letters.join('').toUpperCase() || '?'
}

/** A day as the directory names it, such as 21 Oct 2026. */
export const shortDate = (value: string, lang: string): string =>
  new Date(value).toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' })

/** One of four restrained grounds for a person's initials, the same for them everywhere; it tells nothing about them. */
export function avatarTint(id: string): number {
  let hash = 0
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  return hash % 4
}

/** A name with the titles its user gives on their profile, as in "Prof. Dr. Ada Example, PhD". */
export function fullName({ name, honorific_prefix, honorific_suffix }: Pick<Person, 'name' | 'honorific_prefix' | 'honorific_suffix'>): string {
  const titled = [honorific_prefix, name].filter(Boolean).join(' ')
  return honorific_suffix ? `${titled}, ${honorific_suffix}` : titled
}

/**
 * What stands beside a user's name: their sites or that they learn, and whether they moderate the forum (FOR-08).
 * Each once: two sites may share a name.
 */
export function standing(person: Person, t: (key: MessageKey) => string): string[] {
  return [...new Set([...person.sites, ...(person.learner ? [t('forumLearner')] : []), ...(person.moderator ? [t('forumModerator')] : [])])]
}

export const profilePath = (userId: string) => `/people/${userId}`

const DAY_MS = 86_400_000

/**
 * When an account last signed in, as the directory says it: today with its time, yesterday, or the date.
 * Calendar days of the viewer's clock, so a sign-in late last night is yesterday's.
 */
export function signedInText(value: string, lang: string, t: (key: MessageKey, vars?: Record<string, string>) => string, now = new Date()): string {
  const at = new Date(value)
  const day = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  const days = Math.round((day(now) - day(at)) / DAY_MS)
  if (days === 0) return t('usersToday', { time: at.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }) })
  if (days === 1) return t('usersYesterday')
  return shortDate(value, lang)
}
